import {
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { FC, useEffect } from 'react';
import * as Yup from 'yup';
import { Formik, Field, Form } from 'formik';
import { toast } from 'sonner';
import { useAgencyStore, useRegionStore } from 'Application/store';
import { SubTitleText } from 'Application/components/SubTitleText';
import { IntermediaryTypes, UserRole } from 'Application/models';
import { ThemeMode } from 'config';
import { getUserRegionFromStorage, getUserRoleNameFromStorage, validatePhoneNumber } from 'Application/utils';
import MuiPhoneNumber from 'mui-phone-number';

interface AgencyDetailsProps {
    setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export const AgencyDetails: FC<AgencyDetailsProps> = ({ setCurrentPage, setRowsPerPage }) => {
    const { createAgency, isLoading } = useAgencyStore();
    const userRole = getUserRoleNameFromStorage();
    const userRegion = getUserRegionFromStorage();
    // console.log(userRegion,"userll")
    const { regions, getRegions, isLoading: isRegionsLoading } = useRegionStore();

    const userRegionDetails = regions?.find((region) => region.name === userRegion);
    const userCountryAbbreviation = userRegionDetails?.countryAbbreviation.toLowerCase();

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Agency name is required'),
        emailAddress: Yup.string().email('Invalid email').required('Agency Email is required'),
        phoneNumber: Yup.string()
            .required('Phone Number is required')
            .test('is-valid-number', 'Please enter a valid phone number', (value) => {
                return validatePhoneNumber(value);
            }),
        address: Yup.string().required('Address is required'),
        intermediaryTypes: Yup.string().required('Intermediary type is required'),
        agentNumber: Yup.string().when('intermediaryTypes', {
            is: (val: string) => val === IntermediaryTypes.TRAVEL_AGENCY,
            then: (schema) => schema.optional(),
            otherwise: (schema) => schema.required('Agent Number is required')
        }),
        branchCode: Yup.string().when('intermediaryTypes', {
            is: (val: string) => val === IntermediaryTypes.TRAVEL_AGENCY,
            then: (schema) => schema.optional(),
            otherwise: (schema) => schema.required('Branch Code is required')
        }),
        regionName: Yup.string().when([], {
            is: () => userRole === UserRole.ROLE_ADMINISTRATOR,
            then: (schema) => schema.required('Region is required'),
            otherwise: (schema) => schema.optional()
        }),
        aimsClientNumber: Yup.string().when(['intermediaryTypes', 'regionName'], {
            is: (intermediaryType: string, regionName: string) =>
                intermediaryType === IntermediaryTypes.TRAVEL_AGENCY && regionName === 'Kenya',
            then: (schema) => schema.required('AIMS Client Number Is required'),
            otherwise: (schema) => schema.optional()
        }),
        taxNumber: Yup.string().when(['intermediaryTypes', 'regionName'], {
            is: (intermediaryType: string, regionName: string) =>
                intermediaryType === IntermediaryTypes.TRAVEL_AGENCY && regionName === 'Kenya',
            then: (schema) => schema.required('KRA Pin Number is required'),
            otherwise: (schema) => schema.optional()
        }),
        allowLocalTravel: Yup.string().optional()
    });

    useEffect(() => {
        getRegions();
    }, []);

    const { palette } = useTheme();
    const textColor = palette.mode === ThemeMode.DARK ? '#718096' : '#000';

    // Form submit handler
    const handleAgencySetupSubmit = async (values: any, { setSubmitting }: any) => {
        try {
            const agencyData = {
                agency: {
                    name: values.name,
                    address: values.address,
                    emailAddress: values.emailAddress,
                    phoneNumber: values.phoneNumber,
                    agentNumber: values.agentNumber,
                    branchCode: values.branchCode,
                    intermediaryType: values.intermediaryTypes,
                    regionName: values.regionName === '' ? null : values.regionName,
                    ...(userRegion === 'Kenya' &&
                        values.intermediaryTypes === IntermediaryTypes.TRAVEL_AGENCY && {
                            aimsClientNumber: values.aimsClientNumber,
                            taxNumber: values.taxNumber
                        }),
                    isLocalTravelAllowed: values.allowLocalTravel === 'Yes' ? true : false
                }
            };

            // Pass structured agencyData to createAgency function
            await createAgency(agencyData);

            // Set current page and rows per page to initial states
            setCurrentPage(1);
            setRowsPerPage(10);
            toast.success('Agency created successfully');
        } catch (error: any) {
            // Display the error message in the toast
            const errorMessage = error.message || 'Failed to create Agency';
            console.error('Error creating agency:', errorMessage);
            toast.error(`Error creating agency: ${errorMessage}`);
        } finally {
            setSubmitting(false); // Re-enable submit button after processing
        }
    };

    return (
        <Box className="space-y-3">
            <SubTitleText text="Intermediary Setup" />
            <Typography sx={{ color: textColor }}>Please fill in the following details</Typography>
            <Box>
                <Formik
                    initialValues={{
                        name: '',
                        emailAddress: '',
                        phoneNumber: '',
                        agentNumber: '',
                        address: '',
                        branchCode: '',
                        intermediaryTypes: IntermediaryTypes.TRAVEL_AGENCY,
                        regionName: '',
                        aimsClientNumber: '',
                        taxNumber: '',
                        allowLocalTravel: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleAgencySetupSubmit}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values }) => (
                        <Form>
                            <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                Intermediary Name
                            </Typography>
                            <Field
                                as={TextField}
                                id="name"
                                name="name"
                                label="Intermediary name"
                                fullWidth
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                className="mb-4 mt-2 text-[12px]"
                            />
                            <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                Email
                            </Typography>
                            <Field
                                as={TextField}
                                id="emailAddress"
                                name="emailAddress"
                                label="Email"
                                fullWidth
                                error={touched.emailAddress && Boolean(errors.emailAddress)}
                                helperText={touched.emailAddress && errors.emailAddress}
                                className="mb-4 mt-2 text-[12px]"
                            />

                            <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                Phone Number
                            </Typography>
                            <MuiPhoneNumber
                                id="phoneNumber"
                                name="phoneNumber"
                                fullWidth
                                defaultCountry={userCountryAbbreviation}
                                variant="outlined"
                                onChange={(value) => setFieldValue('phoneNumber', value)}
                                error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                helperText={touched.phoneNumber && errors.phoneNumber}
                                className="mb-4 mt-2 text-[12px]"
                            />
                            <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                Intermediary Type
                            </Typography>
                            <Select
                                className="mb-2"
                                value={values.intermediaryTypes}
                                onChange={(e: SelectChangeEvent<IntermediaryTypes>) =>
                                    setFieldValue('intermediaryTypes', e.target.value as IntermediaryTypes)
                                }
                                fullWidth
                            >
                                <MenuItem value={IntermediaryTypes.TRAVEL_AGENCY}>Travel Agency</MenuItem>
                                <MenuItem value={IntermediaryTypes.INSURANCE_BROKER}>Insurance Broker</MenuItem>
                                <MenuItem value={IntermediaryTypes.FINANCIAL_ADVISOR}>Financial Advisor</MenuItem>
                                <MenuItem value={IntermediaryTypes.INDEPENDENT_FINANCIAL_ADVISOR}>Independent Financial Advisor</MenuItem>
                            </Select>
                            {(values.intermediaryTypes === IntermediaryTypes.INSURANCE_BROKER ||
                                values.intermediaryTypes === IntermediaryTypes.INDEPENDENT_FINANCIAL_ADVISOR ||
                                values.intermediaryTypes === IntermediaryTypes.FINANCIAL_ADVISOR) && (
                                <>
                                    <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                        Agent Number
                                    </Typography>
                                    <Field
                                        as={TextField}
                                        id="agentNumber"
                                        name="agentNumber"
                                        label="Agent Number"
                                        fullWidth
                                        error={touched.agentNumber && Boolean(errors.agentNumber)}
                                        helperText={touched.agentNumber && errors.agentNumber}
                                        className="mb-4 mt-2 text-[12px]"
                                    />
                                    <Typography className="mb-2 text-[13px]">Branch Code</Typography>
                                    <Field
                                        as={TextField}
                                        id="branchCode"
                                        name="branchCode"
                                        label="Branch Code"
                                        fullWidth
                                        error={touched.branchCode && Boolean(errors.branchCode)}
                                        helperText={touched.branchCode && errors.branchCode}
                                        className="mb-4 mt-2 text-[12px]"
                                    />
                                </>
                            )}

                            <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                Address
                            </Typography>
                            <Field
                                as={TextField}
                                id="address"
                                name="address"
                                label="Address"
                                fullWidth
                                error={touched.address && Boolean(errors.address)}
                                helperText={touched.address && errors.address}
                                className="mb-4 mt-2 text-[12px]"
                            />

                            {userRole === UserRole.ROLE_ADMINISTRATOR && (
                                <>
                                    <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                        Region
                                    </Typography>
                                    <FormControl fullWidth className="mb-2">
                                        <Select
                                            fullWidth
                                            name="regionName"
                                            onChange={(e: SelectChangeEvent) => {
                                                setFieldValue('regionName', e.target.value);
                                            }}
                                            error={touched.regionName && Boolean(errors.regionName)}
                                        >
                                            {isRegionsLoading ? (
                                                <MenuItem disabled>Loading...</MenuItem>
                                            ) : (
                                                regions?.map((region) => {
                                                    const { name, regionID } = region;

                                                    return (
                                                        <MenuItem key={regionID} value={name}>
                                                            {name}
                                                        </MenuItem>
                                                    );
                                                })
                                            )}
                                        </Select>
                                        <FormHelperText sx={{ color: '#FF0000' }}>{touched.regionName && errors.regionName}</FormHelperText>
                                    </FormControl>
                                </>
                            )}

                            {values.intermediaryTypes === IntermediaryTypes.TRAVEL_AGENCY && userRegion === 'Kenya' && (
                                <>
                                    <Typography sx={{ color: textColor }} className="mt-2 mb-2 text-[13px]">
                                        AIMS Client Number
                                    </Typography>
                                    <Field
                                        as={TextField}
                                        id="aimsClientNumber"
                                        name="aimsClientNumber"
                                        label="AIMS Client Number"
                                        fullWidth
                                        error={touched.aimsClientNumber && Boolean(errors.aimsClientNumber)}
                                        helperText={touched.aimsClientNumber && errors.aimsClientNumber}
                                        className="mb-4 mt-2 text-[12px]"
                                    />
                                    <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                        KRA Pin Number
                                    </Typography>
                                    <Field
                                        as={TextField}
                                        id="taxNumber"
                                        name="taxNumber"
                                        label="KRA Pin Number"
                                        fullWidth
                                        error={touched.taxNumber && Boolean(errors.taxNumber)}
                                        helperText={touched.taxNumber && errors.taxNumber}
                                        className="mb-4 mt-2 text-[12px]"
                                    />
                                </>
                            )}

                            {values.intermediaryTypes === IntermediaryTypes.TRAVEL_AGENCY && (
                                <>
                                    <Typography sx={{ color: textColor }} className="mb-2 text-[13px]">
                                        Allow local travel
                                    </Typography>
                                    <Select
                                        className="mb-2"
                                        value={values.allowLocalTravel}
                                        onChange={(e: SelectChangeEvent) => setFieldValue('allowLocalTravel', e.target.value)}
                                        fullWidth
                                    >
                                        <MenuItem value="Yes">Yes</MenuItem>
                                        <MenuItem value="No">No</MenuItem>
                                    </Select>
                                </>
                            )}

                            <Divider className="mt-3 mb-4" />
                            <Box className="flex justify-end items-center">
                                <Button
                                    disableElevation
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting || isLoading} // Disable button when submitting
                                >
                                    Submit
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};
